import { supabase } from './supabase'
import { addressFor, suppressedBy } from './emailrules'
import type { Agent } from './types'

/**
 * Talking to the one thing that can actually send.
 *
 * The browser cannot send a blast and must never be able to — the Resend key
 * and the Supabase service key live on Cloudflare, in
 * `functions/api/send-campaign.ts`. This file is the client half: it works out
 * what to show Renee before she decides, and it drives the send.
 *
 * **It is not the authority on anything.** Every number computed here is
 * computed again by the server, from the live roster, and if the two disagree
 * the server refuses and hands back its own. That is not belt and braces; it is
 * the point. The gap between "she looked" and "it sent" is where somebody
 * leaves the brokerage.
 *
 * ── WHY THE BROWSER DRIVES IT IN SLICES ───────────────────────────────────
 *
 * A Cloudflare Worker is capped at fifty outbound requests on the free plan.
 * Sending 785 emails in one invocation needed about eight hundred, so it could
 * not finish — it died in a different place every time and left the send half
 * done. Now `start` books the work and each `run` sends two batches and comes
 * back. The loop lives here, which also means the screen has a real number to
 * show instead of a spinner, and closing the laptop halfway leaves a send that
 * resumes rather than one that has to be untangled.
 */

export type SendStage = 'idle' | 'starting' | 'sending' | 'done' | 'stopped' | 'refused'

export type Progress = {
  stage: SendStage
  /** How many rows exist for this send — the audience, as the server sees it. */
  total: number
  sent: number
  pending: number
  /** Claimed moments ago. A send is happening; this is not a problem. */
  inFlight: number
  /**
   * Claimed a while ago and still sitting there — handed over with no answer
   * back. Never resent automatically, because "it timed out" and "it arrived"
   * are the same thing from the sender's side.
   */
  unknown: number
  failed: number
  /** Taken off because they left or were tagged after the send was booked. */
  suppressed: number
  /** True once there is nothing left to try. Unknowns do not hold this back. */
  done: boolean
}

export const NO_PROGRESS: Progress = {
  stage: 'idle', total: 0, sent: 0, pending: 0, inFlight: 0, unknown: 0, failed: 0, suppressed: 0, done: false,
}

export type ServerBreakdown = {
  recipients?: { id: string; email: string }[]
  departed: number
  suppressed: number
  noEmail: number
  missing: number
}

export type SendOutcome = {
  ok: boolean
  progress: Progress
  error?: string
  /** Set when the server's own count differed from the one she typed. */
  serverCount?: number
  breakdown?: ServerBreakdown
  alreadySent?: boolean
  /**
   * A rehearsal — `SEND_DISABLED` is set server-side, so nothing was sent
   * and nothing was saved. `ok: true` here means the rehearsal itself
   * succeeded, NOT that mail went out — `progress.done` stays `false`
   * because there is genuinely no send to be done. Callers that decide
   * between "it sent" and "it needs attention" off `ok && progress.done`
   * alone (see `notifySendResult` in `Bulk.tsx`) would otherwise read a
   * successful rehearsal as a failure — flagged in adversarial review pass
   * 1. Carried through explicitly so a caller can tell the three outcomes
   * (sent / failed / deliberately not sent) apart instead of collapsing
   * the last two.
   */
  dryRun?: boolean
}

type Reply = {
  ok?: boolean
  error?: string
  started?: boolean
  done?: boolean
  /** The server sends the reason as text, and it is shown to her verbatim. */
  stopped?: string | null
  alreadySent?: boolean
  occurrenceId?: number
  sentThisCall?: number
  nothingToClaim?: boolean
  stoppedKind?: string | null
  total?: number
  sent?: number
  pending?: number
  inFlight?: number
  unknown?: number
  failed?: number
  suppressed?: number
  wouldSend?: number
  actual?: number
  typed?: number | null
  breakdown?: ServerBreakdown
  test?: boolean
  to?: string
  dryRun?: boolean
  count?: number
}

/**
 * The saved blast, as the approval screen sees it.
 *
 * `audience.agent_ids` is the list the composer resolved and stored — the
 * people, by name, never a rule to be worked out again. See `blastAudienceRule`
 * in `campaigns.ts` for why that matters.
 */
export type SavedBlast = {
  id: string
  name: string
  subject: string
  body: string
  audience: { agent_ids?: string[]; from?: string; resolved_at?: string } | null
  created_at: string
}

/**
 * `is_enabled` is deliberately NOT on this type.
 *
 * The composer writes `false`, and nothing reads it: what actually stops this
 * endpoint touching the recurring meeting reminder is the `manual-` shape of
 * the id, which no session can flip. Leaving the column in the type and the
 * select would have it read as a guard by the next person along — which is the
 * exact shape of thing this project has now found in its own code twice.
 */

/**
 * What has changed since she saved it.
 *
 * A blast composed on Friday and approved on Monday is three days of roster
 * drift: somebody left, somebody replied asking not to be written to and got
 * tagged. Showing the difference is the whole job of the approval screen — a
 * number that silently changed is worse than no number.
 */
export type Drift = {
  recipients: Agent[]
  departed: Agent[]
  suppressed: Agent[]
  noEmail: Agent[]
  /** Ids in the saved list that are no longer in the roster at all. */
  missing: string[]
  /** How many distinct people the list held when it was saved. */
  saved: number
}

export function driftFor(
  blast: SavedBlast,
  agents: readonly Agent[],
  tagsByAgent: Map<string, string[]>,
): Drift {
  // Deduplicated, exactly as the server does it. A list holding somebody twice
  // would otherwise make the number she confirms a count of entries rather than
  // of people, and the two sides would never agree.
  const ids = [...new Set(blast.audience?.agent_ids ?? [])]
  const byId = new Map(agents.map((a) => [a.rezen_id, a]))
  const out: Drift = { recipients: [], departed: [], suppressed: [], noEmail: [], missing: [], saved: ids.length }
  for (const id of ids) {
    const a = byId.get(id)
    if (!a) {
      out.missing.push(id)
      continue
    }
    if (!a.is_active) {
      out.departed.push(a)
      continue
    }
    if (suppressedBy(tagsByAgent.get(id) ?? [], 'blast')) {
      out.suppressed.push(a)
      continue
    }
    if (!addressFor(a)) {
      out.noEmail.push(a)
      continue
    }
    out.recipients.push(a)
  }
  return out
}

/** Has anything moved since it was saved? Drives whether the screen says so. */
export const hasDrift = (d: Drift) =>
  d.departed.length > 0 || d.suppressed.length > 0 || d.noEmail.length > 0 || d.missing.length > 0

// -------------------------------------------------------------- live ETA

/**
 * One point on the pace curve — how many had gone out, at what wall-clock
 * time. `Bulk.tsx` appends one of these every time `onProgress` fires and
 * hands the whole list to `estimateRemainingMs` below.
 */
export type PaceSample = { t: number; sent: number }

/**
 * A live estimate of time remaining, from the ACTUAL pace of batches already
 * sent THIS run — not a fixed assumption. Walkthrough-flagged 2026-08-23:
 * real throughput depends on Resend's own rate limiting, which is not
 * constant send to send, so a number that does not update as the run goes
 * would drift from reality by the time it mattered.
 *
 * Averaged across every sample since the run (or resume) began, not just the
 * last interval — one unusually fast or slow batch should not swing the
 * estimate wildly from one `onProgress` tick to the next.
 *
 * `null` means "not enough to go on yet" — fewer than two samples, or no
 * measurable time or no sends between the first and the last (a resume can
 * start with `sent` already high and no elapsed time to measure against) —
 * and is the caller's job to turn into "estimating…", never a false zero.
 * `0` is a real, different answer: nothing left to wait for.
 */
export function estimateRemainingMs(samples: readonly PaceSample[], pending: number): number | null {
  if (pending <= 0) return 0
  if (samples.length < 2) return null
  const first = samples[0]
  const last = samples[samples.length - 1]
  const elapsedMs = last.t - first.t
  const sentDelta = last.sent - first.sent
  if (elapsedMs <= 0 || sentDelta <= 0) return null
  const msPerEmail = elapsedMs / sentDelta
  return Math.round(msPerEmail * pending)
}

/**
 * A deliberately rough figure — "about 4 minutes left", not false precision
 * down to the second a pace this variable cannot actually promise. Only ever
 * called with a real number; `null` from the estimate above is the caller's
 * own "estimating…" to show instead.
 */
export function formatEta(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  if (totalSeconds < 60) return 'under a minute left'
  const minutes = Math.round(totalSeconds / 60)
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'} left`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const hourPart = `${hours} hour${hours === 1 ? '' : 's'}`
  return remainingMinutes > 0 ? `about ${hourPart} ${remainingMinutes} min left` : `about ${hourPart} left`
}

// ------------------------------------------------------------------------ the call

async function call(payload: {
  campaignId: string
  mode: 'test' | 'start' | 'run' | 'status'
  typedCount?: number
}): Promise<{ status: number; body: Reply } | { status: 0; body: Reply }> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) {
    return { status: 0, body: { error: 'Your sign-in has expired. Refresh the page and sign in again.' } }
  }

  let res: Response
  try {
    res = await fetch('/api/send-campaign', {
      method: 'POST',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
  } catch {
    /**
     * No answer came back, which is NOT the same as nothing having happened.
     * The send may be running right now. Calling this a failure would invite
     * another press; the honest instruction is to look before pressing.
     */
    return {
      status: 0,
      body: {
        error:
          'The connection dropped before an answer came back. The send may still be running — ' +
          'wait a moment and press Check progress before doing anything else.',
      },
    }
  }

  const text = await res.text()
  try {
    return { status: res.status, body: JSON.parse(text) as Reply }
  } catch {
    /**
     * Cloudflare answers an unrouted path with the app's own index.html, so a
     * missing deployment arrives here as HTML and would otherwise become an
     * unreadable parse error. Naming it is the difference between five minutes
     * and an hour.
     */
    return {
      status: res.status,
      body: {
        error:
          res.status === 404 || text.trimStart().startsWith('<')
            ? 'The sending service is not switched on for this site yet, so nothing was sent. Tell Chris.'
            : `The sending service answered with something unreadable (${res.status}). Nothing is confirmed sent.`,
      },
    }
  }
}

const progressFrom = (b: Reply, stage: SendStage): Progress => ({
  stage,
  total: b.total ?? 0,
  sent: b.sent ?? 0,
  pending: b.pending ?? 0,
  inFlight: b.inFlight ?? 0,
  unknown: b.unknown ?? 0,
  failed: b.failed ?? 0,
  suppressed: b.suppressed ?? 0,
  done: b.done === true,
})

/** Send a test to yourself. Writes nothing, and cannot reach anybody else. */
export async function sendTest(campaignId: string): Promise<{ ok: boolean; to?: string; error?: string }> {
  const r = await call({ campaignId, mode: 'test' })
  if (r.body.error) return { ok: false, error: r.body.error }
  return { ok: true, to: r.body.to }
}

/** Ask what state a send is in, without touching it. */
export async function checkProgress(campaignId: string): Promise<SendOutcome> {
  const r = await call({ campaignId, mode: 'status' })
  if (r.body.error) return { ok: false, progress: NO_PROGRESS, error: r.body.error }
  if (!r.body.started) return { ok: true, progress: NO_PROGRESS }
  const p = progressFrom(r.body, r.body.done ? 'done' : 'stopped')
  return { ok: true, progress: p }
}

/**
 * Permanently stop a send that has started but not finished — "abandon this,
 * don't let it resume on its own, but keep the record of who already got
 * it." Built 2026-09-08, the day a real send ("Team Connect 9/8") stalled at
 * 100 of 783 on Resend's old daily cap and had to be stopped by hand in the
 * database, because pressing "Carry on" was the only button that existed —
 * see `claude/team-connect-9-8-send-stopped-2026-09-08.md`.
 *
 * Writes the same `last_stopped_kind = 'fatal'` marker the server already
 * writes on a genuine fatal failure (`send-campaign.ts`), so
 * `run-bulk-send.ts`'s cron Worker skips this occurrence on every future
 * tick exactly as it does today's real stops, and `watchdog.ts` already
 * knows to alert on it once rather than every twenty minutes. This does not
 * touch `campaign_sends` or either count on the occurrence — what already
 * went out stays recorded exactly as it happened, and a human can still
 * press "Carry on" afterward if it's ever worth finishing on purpose.
 *
 * Client-writable directly, not through a Function: `campaign_occurrences_all`
 * (`20260813120100_rls.sql`) grants any signed-in app user full read/write
 * here, the same policy that already lets `Bulk.tsx` delete an unstarted
 * campaign straight from the browser.
 */
export async function stopSend(campaignId: string): Promise<string | null> {
  const { error } = await supabase
    .from('campaign_occurrences')
    .update({ last_stopped_kind: 'fatal', last_stopped_at: new Date().toISOString() })
    .eq('campaign_id', campaignId)
  return error ? error.message : null
}

/**
 * The send itself: book it, then work through it a slice at a time.
 *
 * `onProgress` fires after every slice so the screen can count up. The loop
 * stops the moment a slice makes no progress — otherwise a server that is
 * refusing every batch would be asked forever, and the person watching would
 * see a number that never moves and no explanation.
 */
export async function runSend(opts: {
  campaignId: string
  typedCount: number
  onProgress: (p: Progress) => void
}): Promise<SendOutcome> {
  const started = await call({
    campaignId: opts.campaignId,
    mode: 'start',
    typedCount: opts.typedCount,
  })

  if (started.body.error) {
    return {
      ok: false,
      progress: NO_PROGRESS,
      error: started.body.error,
      // The server's own count, when it disagreed. The screen asks her to
      // confirm THIS number rather than repeating one it has already refused.
      serverCount: typeof started.body.actual === 'number' ? started.body.actual : undefined,
      breakdown: started.body.breakdown,
      alreadySent: started.body.alreadySent,
    }
  }

  /**
   * A rehearsal stops here, having exercised every check and written nothing.
   * `start` used to leave a real occurrence and 785 real rows behind on a dry
   * run, which is not a rehearsal.
   */
  if (started.body.dryRun) {
    return {
      ok: true,
      progress: { ...NO_PROGRESS, stage: 'done' },
      error:
        'Sending is switched off on this site, so nothing left and nothing was saved. ' +
        `It would have gone to ${started.body.wouldSend ?? 0} agents.`,
      dryRun: true,
    }
  }

  let progress = progressFrom(started.body, 'sending')
  opts.onProgress(progress)

  // A hard ceiling on round trips, so a bug here cannot become an endless loop
  // against the endpoint. At two batches a call this covers 20,000 recipients.
  for (let i = 0; i < 100; i++) {
    const r = await call({ campaignId: opts.campaignId, mode: 'run' })
    if (r.body.error) {
      return { ok: false, progress: { ...progress, stage: 'stopped' }, error: r.body.error }
    }
    progress = progressFrom(r.body, r.body.done ? 'done' : 'sending')
    opts.onProgress(progress)

    if (r.body.dryRun) {
      // Same rehearsal outcome as the `start`-call branch above, reached
      // instead when `SEND_DISABLED` flips ON between the `start` call and
      // a later `run` call for the same send — a mid-flight config change,
      // not the common case, but this branch clearly exists to handle it
      // (its own message text says so), so `dryRun` has to be set here too.
      // Adversarial review pass 2 caught this: the earlier fix only set it
      // on the `start`-call branch, so this one still mislabelled a
      // deliberate rehearsal as "needs attention" in `notifySendResult`.
      return {
        ok: true,
        progress: { ...progress, stage: 'done' },
        error:
          'This site has sending switched off, so nothing left. It would have gone to ' +
          `${r.body.wouldSend ?? 0} in the first batch.`,
        dryRun: true,
      }
    }

    if (progress.done) return { ok: true, progress: { ...progress, stage: 'done' } }

    /**
     * Three different reasons to stop, and they used to be one message.
     *
     * "Nothing left to claim" happens when another tab is already working
     * through this — telling her it broke and to press again produced a button
     * that could never do anything. A rejection is final. Everything else is
     * worth another go.
     */
    if (r.body.nothingToClaim) {
      return {
        ok: true,
        progress: { ...progress, stage: 'stopped' },
        error:
          progress.inFlight > 0
            ? 'A send of this is already running somewhere else — probably another tab. ' +
              'Leave it be and press Check progress in a minute.'
            : 'There is nothing left to send. Press Check progress to see where it got to.',
      }
    }
    if (r.body.stopped) {
      /**
       * A fatal stop is not something another press can fix — a wrong key, a
       * message the service will not accept at all. Telling her to press again
       * would be the third button in this project that can never do anything,
       * and the server already says which kind of stop it was.
       */
      const fatal = r.body.stoppedKind === 'fatal'
      return {
        ok: false,
        progress: { ...progress, stage: 'stopped' },
        error: fatal
          ? `The send stopped — ${r.body.stopped}. Nothing more will go out until this is sorted. ` +
            'Do not press Send again; tell Chris.'
          : `The send stopped partway — ${r.body.stopped}. Anything already sent stays sent and ` +
            'nobody will receive it twice. Press Send again to carry on.',
      }
    }
  }

  return {
    ok: false,
    progress: { ...progress, stage: 'stopped' },
    error: 'That is taking far more rounds than expected, so it stopped. Tell Chris before pressing again.',
  }
}
